import React, { useState, useEffect, useCallback } from "react";
import { Receipt, Plus, Edit2, Trash2, Save, X, Filter, DollarSign, TrendingDown, AlertTriangle, Calendar } from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

const CATEGORIES = [
  { key: "fijo", label: "Fijo", color: "bg-blue-100 text-blue-800", desc: "Renta, luz oficina, nomina, internet" },
  { key: "variable", label: "Variable", color: "bg-amber-100 text-amber-800", desc: "Empaque, etiquetas, material indirecto" },
  { key: "imprevisto", label: "Imprevisto", color: "bg-red-100 text-red-800", desc: "Reparaciones, gastos inesperados" },
];

const SUBCATEGORIES = {
  fijo: ["Renta", "Luz oficina", "Nomina", "Internet", "Telefono", "Seguros", "Contabilidad", "Otro"],
  variable: ["Empaque", "Etiquetas", "Bolsas", "Cajas envio", "Material limpieza", "Gasolina", "Otro"],
  imprevisto: ["Reparacion equipo", "Multas", "Reposicion herramienta", "Otro"],
};

const emptyExpense = {
  category: "variable",
  subcategory: "",
  description: "",
  amount: "",
  date: new Date().toISOString().slice(0, 10),
  recurring: false,
  notes: "",
};

export default function Gastos() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyExpense });
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, sumRes, remRes] = await Promise.all([
        api.get("/expenses"),
        api.get("/expenses/summary?period=month"),
        api.get("/expenses/reminders"),
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
      setReminders(remRes.data);
    } catch (err) {
      toast.error("Error al cargar gastos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ ...emptyExpense });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (exp) => {
    setForm({ ...exp, amount: String(exp.amount || 0) });
    setEditing(exp.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error("Ingresa un monto valido");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editing) {
        await api.put(`/expenses/${editing}`, payload);
        toast.success("Gasto actualizado");
      } else {
        await api.post("/expenses", payload);
        toast.success("Gasto registrado");
      }
      setShowForm(false);
      load();
    } catch {
      toast.error("Error al guardar gasto");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este gasto?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Gasto eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const filtered = filterCat === "all" ? expenses : expenses.filter(e => e.category === filterCat);
  const catInfo = (key) => CATEGORIES.find(c => c.key === key) || CATEGORIES[1];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sm text-slate-400">Cargando gastos...</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="gastos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="gastos-title">Gastos y Egresos</h1>
          <p className="text-sm text-slate-500 mt-1">Control de costos fijos, variables e imprevistos</p>
        </div>
        <button onClick={openNew} data-testid="add-expense-btn"
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors">
          <Plus size={16} /> Nuevo Gasto
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="expenses-summary">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs font-medium mb-1">
              <DollarSign size={14} /> Total del Mes
            </div>
            <div className="text-xl font-bold text-slate-900" data-testid="total-expenses">{formatMXN(summary.total)}</div>
          </div>
          <div className="bg-white rounded-xl border border-blue-200 p-4">
            <div className="flex items-center gap-2 text-blue-600 text-xs font-medium mb-1">
              <Calendar size={14} /> Fijos
            </div>
            <div className="text-xl font-bold text-blue-900" data-testid="fixed-expenses">{formatMXN(summary.by_category?.fijo || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4">
            <div className="flex items-center gap-2 text-amber-600 text-xs font-medium mb-1">
              <TrendingDown size={14} /> Variables
            </div>
            <div className="text-xl font-bold text-amber-900" data-testid="variable-expenses">{formatMXN(summary.by_category?.variable || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-red-200 p-4">
            <div className="flex items-center gap-2 text-red-600 text-xs font-medium mb-1">
              <AlertTriangle size={14} /> Imprevistos
            </div>
            <div className="text-xl font-bold text-red-900" data-testid="unexpected-expenses">{formatMXN(summary.by_category?.imprevisto || 0)}</div>
          </div>
        </div>
      )}

      {/* Recurring Expense Reminders */}
      {reminders && reminders.missing_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="expense-reminders">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-800">
                {reminders.missing_count} gasto(s) recurrente(s) sin registrar este mes
              </h3>
              <div className="mt-2 space-y-1.5">
                {reminders.missing.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-amber-700">
                      <span className="font-medium">{m.subcategory || m.category}</span>
                      {m.description && <span className="text-amber-500 ml-1">({m.description})</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-amber-600">Ultimo: {formatMXN(m.last_amount)}</span>
                      <button onClick={() => {
                        setForm({
                          ...emptyExpense,
                          category: m.category,
                          subcategory: m.subcategory,
                          description: m.description,
                          amount: String(m.last_amount),
                          recurring: true,
                        });
                        setEditing(null);
                        setShowForm(true);
                      }}
                        className="px-2 py-0.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 transition-colors"
                        data-testid={`register-reminder-${i}`}>
                        Registrar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2" data-testid="expense-filters">
        <button onClick={() => setFilterCat("all")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterCat === "all" ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Todos ({expenses.length})
        </button>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setFilterCat(c.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterCat === c.key ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {c.label} ({expenses.filter(e => e.category === c.key).length})
          </button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="expense-form">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">{editing ? "Editar Gasto" : "Nuevo Gasto"}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-md"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Category */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Categoria</label>
                <div className="flex gap-2">
                  {CATEGORIES.map(c => (
                    <button key={c.key} onClick={() => setForm(f => ({ ...f, category: c.key, subcategory: "" }))}
                      data-testid={`cat-${c.key}`}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${form.category === c.key ? "border-brand-500 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Subcategory */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Subcategoria</label>
                <select value={form.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))}
                  data-testid="expense-subcategory"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option value="">Seleccionar...</option>
                  {(SUBCATEGORIES[form.category] || []).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {/* Amount + Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Monto ($)</label>
                  <input type="number" step="0.01" value={form.amount} data-testid="expense-amount"
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="0.00" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Fecha</label>
                  <input type="date" value={form.date} data-testid="expense-date"
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              {/* Description */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Descripcion</label>
                <input type="text" value={form.description} data-testid="expense-description"
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Ej: Pago renta local" />
              </div>
              {/* Recurring */}
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.recurring} data-testid="expense-recurring"
                  onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))}
                  className="rounded border-slate-300" />
                Gasto recurrente (mensual)
              </label>
              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Notas</label>
                <textarea value={form.notes || ""} rows={2} data-testid="expense-notes"
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-expense-btn"
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
                <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expenses List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="expenses-list">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            <Receipt size={32} className="mx-auto mb-2 opacity-40" />
            No hay gastos registrados
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Subcategoria</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Descripcion</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Monto</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(exp => {
                const ci = catInfo(exp.category);
                return (
                  <tr key={exp.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors" data-testid={`expense-row-${exp.id}`}>
                    <td className="px-4 py-3 text-slate-600">{exp.date || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ci.color}`}>{ci.label}</span>
                      {exp.recurring && <span className="ml-1 text-xs text-slate-400">(recurrente)</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{exp.subcategory || "-"}</td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{exp.description || "-"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatMXN(exp.amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(exp)} data-testid={`edit-expense-${exp.id}`}
                          className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-brand-600">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(exp.id)} data-testid={`delete-expense-${exp.id}`}
                          className="p-1.5 hover:bg-red-50 rounded-md text-slate-400 hover:text-red-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
